// /terminal/closing-soon — opportunities closing within 30 days, default-sorted
// by closing date DESCENDING (most-recently-closed first, per Youssef's Sabre
// W3-C feedback — operators want to see what just dropped off the board). Same
// EntityScreen primitive as /horizon, just with a default filter that pins
// urgency. The "AI rail every prospect sees first" per CEO review.
//
// Per CLAUDE.md Iron Rule 11, this route MUST be an EntityScreen — no fork.
// The handful of differences vs /horizon: default closing_date filter, no NL
// composer (this view is intentionally about urgency, not exploration),
// shorter default sort.

import { ApiError, apiFetch, isInferenceUnavailableError } from '@/lib/api';
import { dateOnly } from '@/lib/format';
import { localizedField, useLocale } from '@/lib/locale';
import { useFilterRailLabels } from '@/lib/useFilterRailLabels';
import { EmptyState, Pill } from '@/primitives';
import { useAuth } from '@/state/auth';
import { useInferenceStatus } from '@/state/inference-status';
import type { api as a, filters as f, search as s } from '@eidollon/contracts';
import { sortPinnedFirst, usePinned } from '@eidollon/intel-feed-client';
import { type ColumnDef, EntityScreen, PinStar, type SortState } from '@eidollon/ui';
import { SavedView, type SavedViewFetcher, decode, encode } from '@eidollon/ui-filters';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { z } from 'zod';

/**
 * Default sort for /terminal/closing-soon.
 *
 * Youssef's Sabre feedback (W3-C): the rail should land on most-recently-closed
 * first, not soonest-closing first — operators want to see what just dropped
 * off the board so they can chase or write a debrief. Default sort is
 * `closing_date:desc`. The user can flip to asc by clicking the column.
 *
 * Exported (named) so a unit test can pin the contract without rendering the
 * whole page (TanStack Router + react-query make the route hard to mount in
 * jsdom without a full provider tree).
 */
export const CLOSING_SOON_DEFAULT_SORT_PARAM = 'closing_date:desc' as const;

export function parseClosingSoonSort(raw: string | undefined): SortState {
  const [field, dir] = (raw ?? CLOSING_SOON_DEFAULT_SORT_PARAM).split(':');
  return {
    field: field || 'closing_date',
    dir: dir === 'asc' ? 'asc' : 'desc',
  };
}

const closingSoonSearch = z.object({
  f: z.string().optional(),
  sort: z.string().optional(),
  cursor: z.string().optional(),
});
type ClosingSoonSearch = z.infer<typeof closingSoonSearch>;

export const Route = createFileRoute('/_authed/terminal/closing-soon')({
  validateSearch: closingSoonSearch,
  component: ClosingSoonPage,
});

// Adapter from SavedView's fetcher contract → the terminal's auth-aware
// apiFetch. Module-scoped so the reference is stable across renders.
const savedViewFetcher: SavedViewFetcher = async ({ url, method, body }) =>
  apiFetch<unknown>(url, body !== undefined ? { method, body } : { method });

const closingSoonSchema: f.FilterSchema = {
  entity: 'OPPORTUNITY',
  // Sabre W3-C: most-recently-closed first so operators see what just dropped.
  defaultSort: { field: 'closing_date', dir: 'desc' },
  fields: [
    {
      id: 'status',
      label: 'STATUS',
      type: 'multi-enum',
      pinned: [{ value: 'active', label: 'active' }],
    },
    {
      id: 'jurisdiction',
      label: 'JURISDICTION',
      type: 'multi-enum',
      pinned: [
        { value: 'US', label: 'US' },
        { value: 'CA', label: 'CA' },
      ],
    },
    {
      id: 'procurement_category',
      label: 'CATEGORY',
      type: 'multi-enum',
    },
    {
      id: 'procurement_method',
      label: 'METHOD',
      type: 'multi-enum',
    },
  ],
};

// Default date window when no `f` URL param is set: now → +30d.
function defaultClosingWindow(): { from: string; to: string } {
  const now = new Date();
  const to = new Date(now.getTime() + 30 * 86_400_000);
  return {
    from: now.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}

function ClosingSoonPage() {
  const navigate = useNavigate({ from: '/terminal/closing-soon' });
  const { locale } = useLocale();
  const filterRailLabels = useFilterRailLabels();
  const search = Route.useSearch() as ClosingSoonSearch;

  // Decode URL filter state. When the URL has nothing, default to
  // `closing_date in next 30 days + status=active`. The user can widen
  // explicitly via the rail; we don't override their saved choice.
  const state: f.FilterState = useMemo(() => {
    if (!search.f) {
      const w = defaultClosingWindow();
      return {
        status: ['active'],
        closing_date: { from: w.from, to: w.to },
      };
    }
    try {
      return decode(search.f);
    } catch {
      const w = defaultClosingWindow();
      return { status: ['active'], closing_date: { from: w.from, to: w.to } };
    }
  }, [search.f]);

  const sort: SortState = useMemo(() => parseClosingSoonSort(search.sort), [search.sort]);

  const setState = (next: f.FilterState) => {
    const enc = encode(next);
    const fParam = enc.kind === 'inline' ? enc.value || undefined : undefined;
    navigate({ search: (prev) => ({ ...prev, f: fParam, cursor: undefined }) });
  };
  const setSort = (next: SortState) => {
    navigate({
      search: (prev) => ({ ...prev, sort: `${next.field}:${next.dir}`, cursor: undefined }),
    });
  };

  /* ────────────────────────────────────────────────────────────────────────
   *  Search query — same backend as Horizon. No NL composer here.
   * ──────────────────────────────────────────────────────────────────────── */

  const searchBody: s.SearchRequest = useMemo(
    () => ({
      state,
      sort,
      limit: 100,
      personalize: true,
      ...(locale && locale !== 'en' ? { target_lang: locale } : {}),
    }),
    [state, sort, locale],
  );

  const opps = useQuery({
    queryKey: ['search', 'opportunities', 'closing-soon', searchBody],
    queryFn: async () =>
      apiFetch<{ rows: a.OpportunityRow[]; meta: s.SearchResponseMeta }>(
        '/v1/search/opportunities',
        { method: 'POST', body: searchBody },
      ),
    placeholderData: (prev) => prev,
  });

  const aggBody: s.AggregationsRequest = useMemo(
    () => ({
      state,
      fields: ['status', 'jurisdiction', 'procurement_category', 'procurement_method'],
    }),
    [state],
  );

  const aggs = useQuery({
    queryKey: ['search', 'opportunities', 'aggs', 'closing-soon', aggBody],
    queryFn: async () =>
      apiFetch<s.AggregationsResponse>('/v1/search/opportunities/aggregations', {
        method: 'POST',
        body: aggBody,
      }),
    staleTime: 30_000,
  });

  const facetCounts = useMemo(() => {
    const out: Record<string, { value: string; label?: string; count: number }[]> = {};
    for (const [field, agg] of Object.entries(aggs.data?.fields ?? {})) {
      out[field] = agg.buckets.map((b) => ({ value: b.value, count: b.count }));
    }
    return out;
  }, [aggs.data]);

  /* ────────────────────────────────────────────────────────────────────────
   *  AI Rail — same /v1/ai/suggest hot path as Horizon. CEO mandate: every
   *  surface that lands a prospect for the first time must show real
   *  signal, not cold-start text.
   * ──────────────────────────────────────────────────────────────────────── */

  const aiOffline = useInferenceStatus((st) => st.offline);

  // Tenant-scoped pinned opportunities. Shared store with /horizon and
  // /awards via entityKey='opp' — pin once, see it pinned everywhere the
  // OpportunityRow surfaces.
  const tenantId = useAuth((st) => st.me?.tenant_id ?? null);
  const pin = usePinned({ tenantId, entityKey: 'opp' });

  const ai = useQuery({
    queryKey: ['ai', 'suggest', 'closing-soon'],
    queryFn: async () =>
      apiFetch<s.SuggestResponse>('/v1/ai/suggest', {
        method: 'POST',
        body: { context: 'terminal' } as s.SuggestRequest,
      }),
    staleTime: 60_000,
    retry: (failureCount, err) => !isInferenceUnavailableError(err) && failureCount < 1,
    enabled: !aiOffline,
  });

  /* ────────────────────────────────────────────────────────────────────────
   *  Columns — narrower than Horizon (no NAICS/value/method) because this
   *  view answers one question: "what closes soon and is it close enough
   *  to act on this week".
   * ──────────────────────────────────────────────────────────────────────── */

  const columns: ColumnDef<a.OpportunityRow>[] = useMemo(
    () => [
      {
        id: 'pin',
        label: '',
        width: 24,
        sticky: 'left',
        align: 'center',
        render: (r) => (
          <PinStar
            pinned={pin.pinnedSet.has(r.id)}
            onToggle={() => pin.toggle(r.id)}
            label={pin.pinnedSet.has(r.id) ? 'unpin opportunity' : 'pin opportunity'}
          />
        ),
      },
      {
        id: 'id',
        label: 'ID',
        width: 96,
        render: (r) => (
          <span style={{ color: 'var(--cyan, #2eb8c7)', fontFamily: 'var(--mono)' }}>
            {r.tender_id ?? r.reference_number ?? r.id.slice(0, 8)}
          </span>
        ),
      },
      {
        id: 'jx',
        label: 'JX',
        width: 36,
        render: (r) => r.jurisdiction,
      },
      {
        id: 'agy',
        label: 'AGENCY',
        width: 220,
        render: (r) =>
          localizedField(r as unknown as Record<string, unknown>, 'agency_raw_name', locale) ??
          r.agency_raw_name ??
          '—',
      },
      {
        id: 'title',
        label: 'TITLE',
        width: 'flex',
        render: (r) => {
          const t = localizedField(r as unknown as Record<string, unknown>, 'title', locale);
          return t ?? r.title ?? <span style={{ color: 'var(--fg-3)' }}>—</span>;
        },
      },
      {
        id: 'closing',
        label: 'CLOSE',
        width: 132,
        align: 'right',
        sortable: true,
        sortField: 'closing_date',
        // Plain date only. The days-till-close pill was stripped — operators
        // found it noisy/misleading on rows that had already closed.
        render: (r) => (r.closing_date ? dateOnly(r.closing_date) : '—'),
      },
      {
        id: 'stat',
        label: 'STAT',
        width: 60,
        render: (r) => <StatusPill status={r.status} />,
      },
    ],
    // Pin column reads from `pin.pinnedSet` + `pin.toggle`; locale flips
    // title/agency through localizedField.
    [pin.pinnedSet, pin.toggle, locale],
  );

  const rawRows = opps.data?.rows ?? [];
  // Pinned rows always at the top, in pin-order (newest first). The user's
  // chosen sort applies to the unpinned tail.
  const rows = useMemo(
    () => sortPinnedFirst(rawRows, pin.pinnedIds, (r) => r.id),
    [rawRows, pin.pinnedIds],
  );

  return (
    <EntityScreen<a.OpportunityRow>
      {...filterRailLabels}
      schema={closingSoonSchema}
      state={state}
      onStateChange={setState}
      sort={sort}
      onSortChange={setSort}
      rows={rows}
      rowKey={(r) => r.id}
      columns={columns}
      onRowClick={(r) => navigate({ to: '/brief/$id', params: { id: r.id } })}
      count={rows.length}
      total={opps.data?.meta.total ?? null}
      loading={opps.isFetching}
      hasMore={Boolean(opps.data?.meta.cursor)}
      headerRightSlot={
        <SavedView
          entity="OPPORTUNITY"
          filters={state}
          onLoad={setState}
          fetcher={savedViewFetcher}
        />
      }
      ai={
        aiOffline
          ? null
          : {
              suggestions: ai.data?.suggestions ?? [],
              // W4-C: cold-start moved to /brief/$id. Closing-soon's rail no
              // longer carries the cold-start placeholder.
              coldStart: false,
              onOpen: (s) => navigate({ to: '/brief/$id', params: { id: s.entity_id } }),
              onDismiss: () => {
                /* feedback wiring lives on /horizon for now */
              },
              onMuteCategory: () => {
                /* session hint only */
              },
              initialMode: 'pinned',
            }
      }
      facetCounts={facetCounts}
      // No NL composer here — this view is about urgency, not exploration.
      // Press / takes the user to /horizon (handled at app shell).
      emptyState={
        <EmptyState
          glyph="OPP"
          reason={opps.error ? 'LOAD FAILED' : 'NOTHING CLOSES IN THIS WINDOW'}
          detail={
            opps.error
              ? `the search backend returned an error · id ${opps.error instanceof ApiError && opps.error.requestId ? opps.error.requestId : 'unknown'}`
              : 'widen the closing-date range, drop the status filter, or jump to /horizon for full search.'
          }
          actions={
            <button
              type="button"
              className="btn xs gh"
              onClick={() => {
                const w = defaultClosingWindow();
                setState({ status: ['active'], closing_date: { from: w.from, to: w.to } });
              }}
            >
              RESET TO 30D ACTIVE
            </button>
          }
        />
      }
      meta={{
        backend: opps.data?.meta.backend ?? 'postgres-fts',
        latencyMs: opps.data?.meta.latencyMs,
        fromCache: opps.data?.meta.fromCache ?? false,
        sortLabel: `${sort.field}:${sort.dir}`,
        rightSlot:
          pin.count > 0 ? (
            <span
              className="mono"
              style={{
                fontSize: 10,
                color: 'var(--amber)',
                letterSpacing: 0.4,
                textTransform: 'uppercase',
              }}
              title={`${pin.count} pinned ${pin.count === 1 ? 'opportunity' : 'opportunities'}`}
            >
              {`★ ${pin.count} PINNED`}
            </span>
          ) : null,
      }}
    />
  );
}

function StatusPill({ status }: { status: a.OpportunityRow['status'] }) {
  const tone =
    status === 'active'
      ? 'primary'
      : status === 'awarded'
        ? 'green'
        : status === 'expired'
          ? 'gray'
          : status === 'cancelled'
            ? 'red'
            : 'gray';
  return <Pill tone={tone}>{statusShort(status)}</Pill>;
}

function statusShort(s: a.OpportunityRow['status']): string {
  switch (s) {
    case 'active':
      return 'act';
    case 'expired':
      return 'exp';
    case 'cancelled':
      return 'can';
    case 'awarded':
      return 'awd';
    case 'draft':
      return 'drf';
    default:
      return 'unk';
  }
}
